import { useEffect } from 'react';
import { useAppContext } from '@/hooks/useAppContext';
import { useThemeDefinition } from '@/hooks/useDittoTheme';
import { buildThemeBackground, buildThemeStyle, loadThemeFonts } from '@/lib/dittoTheme';

const THEME_VARS = [
  'background', 'foreground', 'card', 'card-foreground', 'popover',
  'popover-foreground', 'primary', 'primary-foreground', 'secondary',
  'secondary-foreground', 'muted', 'muted-foreground', 'accent',
  'accent-foreground', 'border', 'input', 'ring',
];

const BG_PROPS = [
  'background-color', 'background-image', 'background-size',
  'background-repeat', 'background-position', 'background-attachment',
] as const;

/**
 * Applies the app-wide Ditto theme (kind 36767 theme definition chosen in
 * Settings) to the document. Colors and fonts are applied via CSS variables
 * on <html>; a theme background image goes on <body> with the theme's
 * background color as a translucent overlay. Ditto's rounded UI element
 * styling is intentionally not applied.
 */
export function DittoThemeApplicator() {
  const { config } = useAppContext();
  const addr = config.dittoThemeAddr || undefined;
  const { data: theme } = useThemeDefinition(addr);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    if (!theme) return;

    // Color variables
    const style = buildThemeStyle(theme) as Record<string, string>;
    for (const [key, value] of Object.entries(style)) {
      root.style.setProperty(key, value);
    }

    // Fonts
    const fonts = loadThemeFonts(theme);
    if (fonts.bodyFamily) {
      root.style.setProperty('font-family', `'${fonts.bodyFamily}', ui-sans-serif, system-ui, sans-serif`);
    }

    // Background image with translucent theme-color overlay
    if (theme.bg) {
      const bg = buildThemeBackground(theme);
      body.style.setProperty('background-color', String(bg.backgroundColor));
      body.style.setProperty('background-image', String(bg.backgroundImage));
      body.style.setProperty('background-size', String(bg.backgroundSize));
      body.style.setProperty('background-repeat', String(bg.backgroundRepeat));
      body.style.setProperty('background-position', String(bg.backgroundPosition));
      body.style.setProperty('background-attachment', 'fixed');
    }

    return () => {
      for (const v of THEME_VARS) root.style.removeProperty(`--${v}`);
      root.style.removeProperty('font-family');
      for (const p of BG_PROPS) body.style.removeProperty(p);
    };
  }, [theme]);

  return null;
}
